import { Injectable, Signal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AgentChatContext } from './agents-chat.model';

// Derives the agent's context from the current route (ids from route params), so pages don't wire
// it up.
@Injectable({ providedIn: 'root' })
export class AgentContextService {
  private readonly router = inject(Router);
  private readonly currentContext = signal<AgentChatContext>({ context: 'general' });

  readonly context: Signal<AgentChatContext> = this.currentContext.asReadonly();

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.currentContext.set(this.derive()));

    this.currentContext.set(this.derive());
  }

  private derive(): AgentChatContext {
    const url = this.router.url.split('?')[0].split('#')[0];
    const params = this.collectParams();
    const campaignId = toPositiveNumber(params.get('campaignId'));
    const widgetId = toPositiveNumber(params.get('widgetId'));

    if (isEditorUrl(url)) {
      if (campaignId !== undefined && widgetId !== undefined) {
        // stateId is part of the editor's runtime state, not the route, so it stays null until the
        // editor feeds it in.
        return { context: 'editor', campaignId, widgetId, stateId: null };
      }
      return { context: 'general' };
    }

    if (isCampaignPerformanceUrl(url) && campaignId !== undefined) {
      return { context: 'analytics', campaignId };
    }

    if (isCampaignSettingsUrl(url) && campaignId !== undefined) {
      return { context: 'campaign', campaignId };
    }

    if (isSiteAnalyticsUrl(url)) {
      return { context: 'dashboard', view: 'analytics' };
    }

    return { context: 'general' };
  }

  private collectParams(): Map<string, string> {
    const params = new Map<string, string>();
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      for (const key of Object.keys(route.params)) {
        params.set(key, route.params[key]);
      }
      route = route.firstChild;
    }
    return params;
  }
}

function isEditorUrl(url: string): boolean {
  return (
    /\/campaigns\/.+\/widgets\/[^/]+\/editor(\/|$)/.test(url) ||
    /\/campaigns\/static-widgets\/[^/]+\/editor(\/|$)/.test(url)
  );
}

function isCampaignPerformanceUrl(url: string): boolean {
  return /\/campaigns\/[^/]+\/performance(\/|$)/.test(url);
}

function isCampaignSettingsUrl(url: string): boolean {
  return /\/campaigns\/.*settings(\/|$)/.test(url);
}

function isSiteAnalyticsUrl(url: string): boolean {
  return url.startsWith('/analytics') || url.includes('web-analytics');
}

function toPositiveNumber(value: string | undefined): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
